import React, { useState, useEffect } from 'react';
import { CreditCard, Key, Eye, EyeOff, Save, Trash2, Shield, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StripeSettings = () => {
  const [config, setConfig] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    is_live_mode: false,
    enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [existingConfig, setExistingConfig] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/stripe/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExistingConfig(data);
        setConfig(prev => ({
          ...prev,
          publishable_key: data.publishable_key || '',
          is_live_mode: data.is_live_mode || false,
          enabled: data.enabled || false
        }));
      }
    } catch (error) {
      console.error('Error fetching Stripe config:', error);
      toast.error('Failed to load Stripe configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Build update payload - only include non-empty values
      const payload = {};
      if (config.publishable_key) payload.publishable_key = config.publishable_key;
      if (config.secret_key) payload.secret_key = config.secret_key;
      if (config.webhook_secret) payload.webhook_secret = config.webhook_secret;
      payload.is_live_mode = config.is_live_mode;
      payload.enabled = config.enabled;

      const response = await fetch(`${API_URL}/api/stripe/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Stripe configuration saved successfully');
        // Clear sensitive fields after save
        setConfig(prev => ({ ...prev, secret_key: '', webhook_secret: '' }));
        fetchConfig(); // Refresh to get updated state
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving Stripe config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the Stripe configuration? This will disable payment processing.')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/stripe/config`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Stripe configuration deleted');
        setConfig({
          publishable_key: '',
          secret_key: '',
          webhook_secret: '',
          is_live_mode: false,
          enabled: false
        });
        setExistingConfig(null);
      } else {
        toast.error('Failed to delete configuration');
      }
    } catch (error) {
      console.error('Error deleting Stripe config:', error);
      toast.error('Failed to delete configuration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-purple-600" />
            Stripe Configuration
          </h1>
          <p className="text-gray-500 mt-1">Configure your Stripe API keys for payment processing</p>
        </div>
        <div className="flex items-center gap-2">
          {existingConfig?.enabled ? (
            <Badge className="bg-green-100 text-green-700 border-green-300">
              <CheckCircle className="w-3 h-3 mr-1" /> Active
            </Badge>
          ) : (
            <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>
          )}
          {existingConfig?.is_live_mode ? (
            <Badge className="bg-red-100 text-red-700 border-red-300">
              <AlertTriangle className="w-3 h-3 mr-1" /> Live Mode
            </Badge>
          ) : (
            <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">Test Mode</Badge>
          )}
        </div>
      </div>

      {/* Warning Banner for Live Mode */}
      {config.is_live_mode && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-800">Live Mode Enabled</h4>
            <p className="text-sm text-red-600">Real payments will be processed. Make sure your keys are correct.</p>
          </div>
        </div>
      )}

      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            API Keys
          </CardTitle>
          <CardDescription>
            Get your API keys from the{' '}
            <a 
              href="https://dashboard.stripe.com/apikeys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Stripe Dashboard
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Publishable Key */}
          <div className="space-y-2">
            <Label htmlFor="publishable_key">Publishable Key</Label>
            <Input
              id="publishable_key"
              type="text"
              placeholder="pk_test_... or pk_live_..."
              value={config.publishable_key}
              onChange={(e) => setConfig(prev => ({ ...prev, publishable_key: e.target.value }))}
              className="font-mono text-sm"
            />
            <p className="text-xs text-gray-500">This key is safe to expose in frontend code</p>
          </div>

          {/* Secret Key */}
          <div className="space-y-2">
            <Label htmlFor="secret_key">Secret Key</Label>
            <div className="relative">
              <Input
                id="secret_key"
                type={showSecretKey ? 'text' : 'password'}
                placeholder={existingConfig?.secret_key_set ? '••••••••••••••••' : 'sk_test_... or sk_live_...'}
                value={config.secret_key}
                onChange={(e) => setConfig(prev => ({ ...prev, secret_key: e.target.value }))}
                className="font-mono text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSecretKey(!showSecretKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {existingConfig?.secret_key_set && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Secret key is configured ({existingConfig.secret_key_preview})
              </p>
            )}
            <p className="text-xs text-gray-500">Keep this key secure. Never expose it in frontend code.</p>
          </div>

          {/* Webhook Secret (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="webhook_secret">Webhook Secret (Optional)</Label>
            <div className="relative">
              <Input
                id="webhook_secret"
                type={showWebhookSecret ? 'text' : 'password'}
                placeholder={existingConfig?.webhook_secret_set ? '••••••••••••••••' : 'whsec_...'}
                value={config.webhook_secret}
                onChange={(e) => setConfig(prev => ({ ...prev, webhook_secret: e.target.value }))}
                className="font-mono text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {existingConfig?.webhook_secret_set && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Webhook secret is configured
              </p>
            )}
            <p className="text-xs text-gray-500">Required for webhook signature verification</p>
          </div>
        </CardContent>
      </Card>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Stripe */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="enabled" className="text-base font-medium">Enable Stripe Payments</Label>
              <p className="text-sm text-gray-500">Allow customers to pay with credit cards</p>
            </div>
            <Switch
              id="enabled"
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {/* Live Mode Toggle */}
          <div className="flex items-center justify-between border-t pt-6">
            <div>
              <Label htmlFor="live_mode" className="text-base font-medium flex items-center gap-2">
                Live Mode
                <Badge variant="outline" className="text-red-600 border-red-300">Production</Badge>
              </Label>
              <p className="text-sm text-gray-500">Switch between test and live API keys</p>
            </div>
            <Switch
              id="live_mode"
              checked={config.is_live_mode}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, is_live_mode: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={handleDelete}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          disabled={!existingConfig?.publishable_key}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Reset Configuration
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !config.publishable_key}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Configuration
        </Button>
      </div>

      {/* Help Section */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h4 className="font-semibold text-blue-900 mb-2">Need Help?</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Get Test API Keys</a></li>
            <li>• <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Get Live API Keys</a></li>
            <li>• Test card number: <code className="bg-blue-100 px-1 rounded">4242 4242 4242 4242</code></li>
            <li>• Use any future expiry date and any 3-digit CVC</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default StripeSettings;
